import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Film, Loader2, Sparkles, Wand2, UserCircle2, Coins, Lock, AlertTriangle, MessageSquare, Volume2, Music2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { VIDEO_STYLES, VIDEO_DURATIONS } from "@/lib/options";
import { videoCredits, dialogueSceneCredits, fmtCredits } from "@/lib/cost";
import { preflightWarnings } from "@/lib/preflight";
import { useDraft, clearDraft } from "@/lib/useDraft";
import { Chip } from "@/components/Chip";
import StatusBadge from "@/components/StatusBadge";
import LongVideoPanel from "@/pages/panels/LongVideoPanel";

export default function VideoPanel({ projects, characters, reload }) {
  const [title, setTitle] = useDraft("mfp_draft_video_title", "");
  const [mode, setMode] = useState("write");
  const [prompt, setPrompt] = useDraft("mfp_draft_video_prompt", "");
  const [idea, setIdea] = useDraft("mfp_draft_video_idea", "");
  const [style, setStyle] = useDraft("mfp_draft_video_style", "cinematic");
  const [duration, setDuration] = useDraft("mfp_draft_video_duration", "6");
  const [characterId, setCharacterId] = useState(null);
  const [takes, setTakes] = useState(2);
  const [quality, setQuality] = useDraft("mfp_draft_video_quality", "pro");
  const [dialogueMode, setDialogueMode] = useState(false);
  const [dialogue, setDialogue] = useState("");
  const [genDesc, setGenDesc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastId, setLastId] = useState(null);
  const [adv, setAdv] = useState(false);
  const [panelMode, setPanelMode] = useState("clip");
  useEffect(() => { api.get("/config").then((r) => setAdv(!!r.data.advanced_scenes)).catch(() => {}); }, []);

  const current = projects.find((p) => p.id === lastId);
  const selectedChar = characters.find((c) => c.id === characterId);
  const canDialogue = !!(selectedChar && selectedChar.photo_path && selectedChar.voice_path);
  const dialogueOn = dialogueMode && canDialogue;
  const durOptions = quality === "standard" ? [["6", "6 seg"], ["10", "10 seg"]] : [["5", "5 seg"], ["10", "10 seg"]];
  const durOk = durOptions.some(([id]) => id === duration) ? duration : durOptions[0][0];
  const warnings = preflightWarnings({ hasCharacter: !!characterId, prompt, duration: durOk, quality });

  const doGenDesc = async () => {
    if (!idea.trim()) { toast.error("Escribe una idea primero"); return; }
    setGenDesc(true);
    try {
      const r = await api.post("/ai/scene", { idea, style });
      setPrompt(r.data.description);
      setMode("write");
      toast.success("Descripción generada");
    } catch { toast.error("No se pudo generar la descripción"); }
    finally { setGenDesc(false); }
  };

  const submit = async () => {
    if (dialogueOn) {
      if (!dialogue.trim()) { toast.error("Escribe lo que dice el personaje"); return; }
    } else if (!prompt.trim()) { toast.error("Describe la escena"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/videos", {
        title: title.trim() || "Mi video",
        prompt: prompt || (dialogueOn ? `${selectedChar?.name || "Personaje"} habla a cámara` : ""),
        style, duration: durOk, character_id: characterId, variants: takes, quality,
        mode: dialogueOn ? "dialogue" : "normal", dialogue: dialogueOn ? dialogue.trim() : "",
        idempotency_key: crypto.randomUUID(),
      });
      setLastId(r.data.id);
      await reload();
      ["title", "prompt", "idea"].forEach((k) => clearDraft(`mfp_draft_video_${k}`));
      setTitle(""); setPrompt(""); setIdea(""); setDialogue("");
      toast.success(dialogueOn ? "Generando video con voz y lip-sync nativo…" : "Renderizando tu video…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-up">
      <div className="flex items-center gap-3">
        <Film className="w-6 h-6 text-[#FF334B]" />
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Crear video</h2>
      </div>

      <div className="flex gap-2 p-1 rounded-full border border-white/10 bg-[#0B0C13] w-full sm:w-fit" data-testid="video-panel-mode">
        <button data-testid="video-mode-clip" onClick={() => setPanelMode("clip")}
          className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${panelMode === "clip" ? "bg-[#FF334B] text-white" : "text-[#94A3B8] hover:text-white"}`}>
          <Film className="w-4 h-4" /> Clip corto
        </button>
        <button data-testid="video-mode-long" onClick={() => setPanelMode("largo")}
          className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${panelMode === "largo" ? "bg-[#FF334B] text-white" : "text-[#94A3B8] hover:text-white"}`}>
          <Music2 className="w-4 h-4" /> Video largo con tu audio
        </button>
      </div>

      {panelMode === "largo" ? (
        <LongVideoPanel projects={projects} characters={characters} reload={reload} />
      ) : (
      <div className="space-y-6">
      <div className="mfp-card p-6 space-y-6">
        <div>
          <label className="mono-label">Título</label>
          <input data-testid="video-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Nombre de tu video"
            className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors" />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <button data-testid="video-mode-write" onClick={() => setMode("write")}
              className={`text-sm px-3 py-1.5 rounded-lg ${mode === "write" ? "bg-white/10 text-white" : "text-[#64748B]"}`}>✍️ Describo la escena</button>
            <button data-testid="video-mode-idea" onClick={() => setMode("idea")}
              className={`text-sm px-3 py-1.5 rounded-lg ${mode === "idea" ? "bg-white/10 text-white" : "text-[#64748B]"}`}>✨ Solo la idea</button>
          </div>
          {mode === "write" ? (
            <textarea data-testid="video-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
              rows={5} placeholder="Describe la escena: qué pasa, ambiente, cámara, iluminación…"
              className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors" />
          ) : (
            <div className="space-y-3">
              <textarea data-testid="video-idea-input" value={idea} onChange={(e) => setIdea(e.target.value)}
                rows={3} placeholder="Describe la idea en pocas palabras…"
                className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] transition-colors" />
              <button data-testid="generate-scene-button" onClick={doGenDesc} disabled={genDesc}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50">
                {genDesc ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4" />}
                Generar descripción con IA
              </button>
              {prompt && <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B] text-sm" />}
            </div>
          )}
        </div>

        <div>
          <label className="mono-label">Estilo</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {VIDEO_STYLES.map((s) => (
              <Chip key={s.id} active={style === s.id} onClick={() => setStyle(s.id)} testId={`style-${s.id}`}>{s.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <label className="mono-label">Personaje (de tu elenco)</label>
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip active={!characterId} onClick={() => setCharacterId(null)} testId="character-none">Sin personaje</Chip>
            {characters.map((c) => (
              <button key={c.id} data-testid={`video-character-${c.id}`} onClick={() => setCharacterId(c.id)}
                className={`flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full border transition-colors ${characterId === c.id ? "bg-[#FF334B] border-[#FF334B] text-white" : "border-white/12 text-[#94A3B8] hover:border-[#FF334B]/50"}`}>
                {c.photo_path
                  ? <img src={mediaUrl(c.photo_path)} alt={c.name} className="w-7 h-7 rounded-full object-cover" />
                  : <UserCircle2 className="w-7 h-7" />}
                <span className="text-sm font-medium">{c.name}</span>
                {c.locked && <Lock className="w-3 h-3 text-[#FF9F2E]" />}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#64748B] mt-2">Con un personaje, la IA genera el video a partir de su foto (imagen → video).{characters.find((c) => c.id === characterId)?.locked ? " ADN bloqueado: se respetará rostro, edad y vestuario." : ""}</p>
        </div>

        {selectedChar && (
          <div>
            <button type="button" data-testid="video-dialogue-toggle" onClick={() => setDialogueMode(!dialogueMode)} disabled={!canDialogue}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${dialogueOn ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/12 hover:border-white/25"} ${!canDialogue ? "opacity-60" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 font-head font-semibold text-sm"><MessageSquare className="w-4 h-4 text-[#00F0FF]" /> Habla en cámara (audio-first)</span>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${dialogueOn ? "bg-[#00F0FF] justify-end" : "bg-white/15"}`}>
                  <span className="w-4 h-4 rounded-full bg-white" />
                </span>
              </div>
              <p className="text-xs text-[#64748B] mt-1">
                {canDialogue ? "La voz nace primero y el video se genera con lip-sync nativo (Kling Avatar Pro)."
                  : `${selectedChar.name} necesita foto y voz para hablar en cámara. Súbelas en Personajes.`}
              </p>
            </button>
            {dialogueOn && (
              <div className="mt-3 space-y-1.5 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/5 p-3" data-testid="video-dialogue-box">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#00F0FF]"><Volume2 className="w-3.5 h-3.5" /> Diálogo de {selectedChar.name}</span>
                <textarea data-testid="video-dialogue-input" value={dialogue} onChange={(e) => setDialogue(e.target.value)} rows={2}
                  placeholder="Lo que dice el personaje (la duración la marca la voz)"
                  className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00F0FF]" />
              </div>
            )}
          </div>
        )}

        <div>
          <label className="mono-label">Calidad</label>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="video-quality">
            <Chip active={quality === "standard"} onClick={() => setQuality("standard")} testId="video-quality-standard">Estándar (rápido)</Chip>
            <Chip active={quality === "pro"} onClick={() => setQuality("pro")} testId="video-quality-pro">Pro · casi real (1080p)</Chip>
            {adv && <Chip active={quality === "cine4k"} onClick={() => setQuality("cine4k")} testId="video-quality-cine4k">Cine 4K · ultra</Chip>}
          </div>
          <p className="text-xs text-[#64748B] mt-2">{quality === "cine4k"
            ? "Kling O3 Cine 4K: resolución 4K real, tope de calidad cinematográfica. Tier premium (cuesta mucho más por clip)."
            : quality === "pro"
            ? "Kling 2.5 Turbo Pro: máxima calidad realista en 1080p, movimiento y anatomía de nivel comercial (recomendada)."
            : "Hailuo-02 Estándar: más económico y rápido, resolución 768p, calidad menor."}</p>
        </div>

        <div>
          <label className="mono-label">Duración del clip</label>
          <div className="mt-3 flex gap-2">
            {durOptions.map(([id, label]) => (
              <Chip key={id} active={durOk === id} onClick={() => setDuration(id)} testId={`video-duration-${id}`}>{label}</Chip>
            ))}
          </div>
          <p className="text-xs text-[#64748B] mt-2">{quality === "standard"
            ? "El modo Estándar entrega clips de 6 o 10 segundos (768p)."
            : "Duraciones que el modelo entrega exactas: 5 o 10 segundos."}</p>
        </div>

        <div>
          <label className="mono-label">Tomas a generar</label>
          <div className="mt-3 flex gap-2">
            <Chip active={takes === 2} onClick={() => setTakes(2)} testId="video-takes-2">2 tomas (elige la mejor)</Chip>
            <Chip active={takes === 1} onClick={() => setTakes(1)} testId="video-takes-1">1 toma</Chip>
          </div>
          <p className="text-xs text-[#64748B] mt-2">Con 2 tomas la IA crea dos versiones del mismo video para que elijas la mejor (tarda el doble).</p>
        </div>

        <div className="flex items-center gap-2 rounded-xl border border-white/8 bg-[#0B0C13] px-4 py-3" data-testid="video-cost-estimate">
          <Coins className="w-4 h-4 text-[#FF9F2E] shrink-0" />
          <span className="text-sm text-[#94A3B8]">
            Costo: <strong className="text-white">{fmtCredits(dialogueOn ? dialogueSceneCredits(Number(durOk)) : videoCredits(Number(durOk), takes, quality))}</strong>
            <span className="text-[#64748B]"> · {dialogueOn ? "diálogo (lip-sync nativo)" : `${takes} ${takes === 1 ? "toma" : "tomas"} · ${quality === "cine4k" ? "Cine 4K" : quality === "pro" ? "Pro 1080p" : "Estándar"}`} · {durOk}s</span>
          </span>
        </div>

        {warnings.length > 0 && (
          <div className="rounded-xl border border-[#FF9F2E]/40 bg-[#FF9F2E]/10 px-4 py-3 space-y-1" data-testid="video-preflight-warnings">
            {warnings.map((w, i) => (
              <p key={i} className="text-xs text-[#FFCF8F] flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {w}
              </p>
            ))}
          </div>
        )}

        <button data-testid="generate-video-button" onClick={submit} disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60">
          {submitting ? <Loader2 className="w-5 h-5 spin" /> : <Sparkles className="w-5 h-5" />}
          Generar video
        </button>
      </div>

      {current && (
        <div className="mfp-card p-6 space-y-4 fade-up" data-testid="video-result">
          <div className="flex items-center justify-between">
            <div className="font-head font-semibold text-lg">{current.title}</div>
            <StatusBadge status={current.status} />
          </div>
          {current.status === "processing" && (
            <div className="flex items-center gap-3 text-[#94A3B8]">
              <Loader2 className="w-5 h-5 spin text-[#FF9F2E]" />
              Renderizando video con IA… (puede tardar 1-4 min)
            </div>
          )}
          {current.status === "failed" && <p className="text-red-400 text-sm">{current.error}</p>}
          {current.status === "ready" && current.result?.video_path && (
            <video data-testid="video-player" src={mediaUrl(current.result.video_path)} controls
              poster={mediaUrl(current.result?.poster_path)}
              className="w-full rounded-xl border border-white/10 bg-black" />
          )}
          {current.status === "ready" && current.result?.variants?.length > 1 && (
            <div className="flex items-center gap-2" data-testid="video-variants">
              <span className="mono-label">Toma:</span>
              {current.result.variants.map((v, i) => (
                <button key={i} data-testid={`video-variant-${i}`}
                  onClick={async () => { try { await api.post(`/projects/${current.id}/variant/select`, { index: i }); await reload(); toast.success("Toma elegida"); } catch { toast.error("No se pudo elegir"); } }}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${current.result.video_path === v.video_path ? "bg-[#FF334B] text-white" : "border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B]"}`}>
                  {v.label || `Toma ${i + 1}`}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      </div>
      )}
    </div>
  );
}

